import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormControlName,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FormUtils } from '../../../../../shared/helpers/formUtils';
import { ETrueFalse, EYesNo } from '../../../../../shared/models/types.enum';
import { DateUtils } from '../../../../../shared/helpers/dateUtils';
@Component({
  selector: 'notifications',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.css',
})
export class NotificationsComponent
  implements OnInit, OnChanges, AfterViewInit
{
  @ViewChildren('datepickerRef') datepickers!: QueryList<ElementRef>;

  @Input() rmsPlanChecklistData: any;
  @Output() onSaveSection2 = new EventEmitter<any>();

  yesNo = EYesNo;
  notificationForm: FormGroup = new FormGroup([]);

  checklist = [
    {
      label: 'District Administration',
      formControlName: 'district_notification',
      selector: 'district',
      informed: '0',
    },
    {
      label: 'Staff and Teachers',
      formControlName: 'staff_teachers_notification',
      selector: 'staff_teachers',
      staff_teachers_informed_notes: '',
    },
    {
      label: 'Community and/or Public Notification(s)',
      formControlName: 'community_notification',
      selector: 'community',
      informed: '0',
    },
    {
      label: 'Law Enforcement',
      formControlName: 'law_enforcement_notification',
      selector: 'law_enforcement',
      informed: '0',
    },
  ];

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['rmsPlanChecklistData'] &&
      changes['rmsPlanChecklistData'].currentValue
    ) {
      this.initializeForm();
    }
  }

  async ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.initializeDatepickers(), 0);  // wait until DOM is ready
    }
  }
  

  private initializeForm(): void {
    const group: any = {};

    this.checklist.forEach((item) => {
      const rawValue = this.rmsPlanChecklistData?.[item.selector + '_informed'];
      const informed = rawValue === null || rawValue === 'null' || rawValue === undefined
        ? item.informed
        : String(rawValue);

      const informant =
        this.rmsPlanChecklistData?.[item.selector + '_informant'] || '';
      const informedDate =
        this.rmsPlanChecklistData?.[item.selector + '_informed_date'] || null;
      const planningNotes =
        this.rmsPlanChecklistData?.[item.selector + '_informed_notes'] || '';

      group[item.formControlName] = new FormControl(
        this.rmsPlanChecklistData?.[item.formControlName] ||
          Number(this.yesNo.No)
      );
      
      if (item.formControlName !== 'staff_teachers_notification') {
        group[item.selector + '_informed'] = new FormControl(informed);
        group[item.selector + '_informant'] = new FormControl(informant);
        group[item.selector + '_informed_date'] = new FormControl(informedDate);
      }
      group[item.selector + '_informed_notes'] = new FormControl(planningNotes);
    });

    group['notifications_roar_needed'] = new FormControl(
      String(
        this.rmsPlanChecklistData?.notifications_roar_needed !== undefined
          ? this.rmsPlanChecklistData.notifications_roar_needed
          : this.yesNo.No
      ),
      Validators.required
    );
    this.notificationForm = new FormGroup(group);

    this.setupFormChangeHandlers();
  }

  // set up flowbite date picker, all dates show up on client view is human readable format, but on save they are converted to backend format
  // and if no date is selected, it will default to current date
  // this method is called after the view is initialized to ensure that the datepicker elements are available in the DOM
  // if user change date, this function will update the form control with the new date in backend format
  async initializeDatepickers() {
    if (isPlatformBrowser(this.platformId)) {
      const { Datepicker } = await import('flowbite-datepicker');
  
      this.datepickers.forEach((datepicker) => {
        const selector = datepicker.nativeElement.getAttribute('data-selector');
        const field = selector + '_informed_date';
  
        if (datepicker && this.notificationForm.get(field)) {
          new Datepicker(datepicker.nativeElement, {
            autohide: true,
            format: 'mm/dd/yyyy',
          });
  
          const defaultValue = this.notificationForm.get(field)?.value;
          // set default value to display for datepicker
          datepicker.nativeElement.value = defaultValue ? DateUtils.formatDateForDisplay(defaultValue) : DateUtils.getCurrentFormattedDate();
  
          // Add event listener for date change
          datepicker.nativeElement.addEventListener('changeDate', (event: any) => {
            const formattedDate = DateUtils.formatDateForBackend(event.target.value);
            this.notificationForm.get(field)?.setValue(formattedDate);
          });
        }
      });
    }
  }
  

  private setupFormChangeHandlers(): void {
    this.checklist.forEach((item) => {
      if (item.formControlName !== 'staff_teachers_notification') {
        const checkboxControl = this.notificationForm.get(item.formControlName);
        const informedControl = this.notificationForm.get(item.selector + '_informed');
        const informantControl = this.notificationForm.get(item.selector + '_informant');
        const informedDateControl = this.notificationForm.get(item.selector + '_informed_date');
        const planningNotesControl = this.notificationForm.get(item.selector + '_informed_notes');

        checkboxControl?.valueChanges.subscribe((value) => {
          const convertedValue = value ? 1 : 0; // Convert true/false to 1/0

          // Avoid unnecessary updates
          if (checkboxControl.value !== value) {
            checkboxControl.setValue(convertedValue, { emitEvent: false });
          }

          if (convertedValue === 1) {
            informedControl?.setValue(this.yesNo.Yes);
            informedControl?.setValidators([Validators.required]);

            if (informedControl?.value === this.yesNo.Yes) {
              if (isPlatformBrowser(this.platformId)) {
                setTimeout(() => this.initializeDatepickers(), 0);  // wait until DOM is ready
              }
              planningNotesControl?.clearValidators();
              planningNotesControl?.setValue(null);
              informedDateControl?.setValue(DateUtils.formatDateForBackend(DateUtils.getCurrentFormattedDate())); // set default date to current date, this is used for sending to backend

              informantControl?.setValidators([Validators.required]);
              informedDateControl?.setValidators([Validators.required]);
            
              informedControl?.updateValueAndValidity();
              informantControl?.updateValueAndValidity();
              informedDateControl?.updateValueAndValidity();
              planningNotesControl?.updateValueAndValidity();
            } else if (informedControl?.value === this.yesNo.No) {
              planningNotesControl?.setValidators([Validators.required]);
              informantControl?.clearValidators();
              informedDateControl?.clearValidators();
              informantControl?.setValue(null);
              informedDateControl?.setValue(null);

              informedControl?.updateValueAndValidity();
              informantControl?.updateValueAndValidity();
              informedDateControl?.updateValueAndValidity();
              planningNotesControl?.updateValueAndValidity();
            }
          } else {
            // Unchecked
            informedControl?.clearValidators();
            informantControl?.clearValidators();
            informedDateControl?.clearValidators();
            planningNotesControl?.clearValidators();

            informedControl?.setValue(null);
            informantControl?.setValue(null);
            informedDateControl?.setValue(null);
            planningNotesControl?.setValue(null);

            informedControl?.updateValueAndValidity();
            informantControl?.updateValueAndValidity();
            informedDateControl?.updateValueAndValidity();
            planningNotesControl?.updateValueAndValidity();
          }
        });

        // Handle Radio Button Changes
        informedControl?.valueChanges.subscribe((value) => {
          if (value === this.yesNo.Yes) {
            planningNotesControl?.clearValidators();
            planningNotesControl?.setValue('');

            informantControl?.setValidators([Validators.required]);
            informedDateControl?.setValidators([Validators.required]);

            if (isPlatformBrowser(this.platformId)) {
              this.initializeDatepickers();
            }
          } else if (value === this.yesNo.No) {
            planningNotesControl?.setValidators([Validators.required]);

            informantControl?.clearValidators();
            informedDateControl?.clearValidators();
            informantControl?.setValue('');
            informedDateControl?.setValue(DateUtils.formatDateForBackend(DateUtils.getCurrentFormattedDate()));
          }

          informantControl?.updateValueAndValidity();
          informedDateControl?.updateValueAndValidity();
          planningNotesControl?.updateValueAndValidity();
        });
      } else {
        // Special case: staff_teachers_notification
        const checkboxControl = this.notificationForm.get(item.formControlName);
        const planningNotesControl = this.notificationForm.get(
          item.selector + '_informed_notes'
        );

        checkboxControl?.valueChanges.subscribe((value) => {
          const convertedValue = value ? 1 : 0;
          if (checkboxControl.value !== convertedValue) {
            checkboxControl.setValue(convertedValue, { emitEvent: false });
          }

          if (convertedValue === 1) {
            planningNotesControl?.setValidators([Validators.required]);
          } else {
            planningNotesControl?.clearValidators();
            planningNotesControl?.setValue('');
          }

          planningNotesControl?.updateValueAndValidity();
        });
      }
    });
  }

  saveNotification(): void {
    if (this.notificationForm.valid) {
      this.onSaveSection2.emit(this.notificationForm.value);
    }
  }

  isAnyCheckboxChecked(): boolean {
    const self = this;
    let res = false;
    this.checklist.forEach(cb => {
      const checkboxFormEl = self.notificationForm.get(cb.formControlName);

      if (checkboxFormEl?.value == true)
      {
        res = true;
      }
    });

    return res;
  }
}